import { clsx } from 'clsx';
import { Languages } from 'lucide-react';
import { useState } from 'react';

import { AidsPanel } from './AidsPanel.tsx';
import { aidsFor, correctionsFor } from './aids.ts';
import { CharacterFace, usePhase } from '../avatar/CharacterFace';
import { LearnerPicture } from '../components/LearnerPicture';
import { t } from '../i18n/t';
import { En } from '../i18n/En';
import { markUp, preview, type Mark } from './transcript.ts';

import type { Line } from './turns.ts';
import type { Register } from '../profile/register';
import type { SessionCharacter } from '../session/context';

/**
 * The face beside a tutor bubble.
 *
 * `CharacterFace` rather than `TutorAvatar`: the boundary between them is the
 * live region, and a transcript of twenty tutor turns would be twenty elements
 * each announcing the session state. A scrollback needs the drawing and none of
 * the announcing — the same reason the select screen uses it.
 *
 * `role="presentation"`, because `chat-header` beside it already carries the
 * name in text. Without that a screen reader meets the character twice per
 * line.
 */
function BubbleFace({
  character,
  expression,
  phase,
}: {
  character: SessionCharacter;
  expression: 'neutral' | 'speaking';
  phase: number;
}) {
  return (
    <div className="chat-image">
      <span className="block w-8" role="presentation">
        <CharacterFace
          artKey={character.artKey}
          accentHex={character.accentHex}
          intensity={character.intensity}
          expression={expression}
          phase={phase}
        />
      </span>
    </div>
  );
}

/**
 * The conversation as it is read back.
 *
 * Its own component because `SessionView` is the session's machinery and this is
 * how it looks; the two change for different reasons, and the one file was
 * carrying both.
 *
 * Three structural defects this replaces, all of them from the markup rather
 * than the styling:
 *
 * - The list was a `<div class="chat">` wrapping per-message `<div class="chat">`.
 *   daisyUI's `chat` *is* the single-message grid, so nesting it put every
 *   bubble inside a second grid that was never meant to contain one.
 * - An `error` line took the tutor's branch of a two-way ternary and rendered
 *   `chat-start chat-bubble-info` — pixel-identical to the tutor speaking. A
 *   failure the session reported was shown as something the tutor said.
 * - The streamed reply was an italic `<p>` outside the chat entirely, so the
 *   tutor's words were unstyled while they arrived and became a bubble only
 *   once `TutorTurnCommitted` replaced them. That transition is what read as
 *   "the tutor's messages have no style".
 *
 * Speaker prefixes are gone from the text. Side and colour carry the speaker
 * visually; `chat-header` carries it in words for the tutor, and an `sr-only`
 * span does for the learner, so removing "You:" costs a screen reader nothing.
 */
export function Transcript({
  lines,
  marks,
  register,
  character,
  nativeLanguage,
  learner,
  reply,
  partial,
  awaiting,
  warming,
}: {
  lines: readonly Line[];
  marks: Record<string, Mark[]>;
  register: Register;
  character: SessionCharacter | null;
  /** The learner's own native language, or null if they never set one. Threads
   * straight into `aidsFor` and into the panel's `Turn.translation` query. */
  nativeLanguage: string | null;
  /** Whose face goes beside the learner's lines (learner picture PRD §3.3). */
  learner: {
    readonly id: string;
    readonly displayName: string;
    readonly picture?: { readonly preset?: string | null; readonly url?: string | null } | null;
  } | null;
  /** The tutor's reply as it streams, before any turn commits it. */
  reply: string;
  /** The learner's words while they are still speaking; empty for none. */
  partial: string;
  /** The tutor has been asked for words that have not started arriving. */
  awaiting: boolean;
  /** That wait is the models loading, not the tutor composing a reply. */
  warming: boolean;
}) {
  // One subscription drives every face on screen — what `usePhase` is for.
  const phase = usePhase();

  // Which line's aids panel is open, kept by turn id rather than array index:
  // an index survives neither a streamed reply committing (which appends a
  // line) nor a re-render reordering nothing but still recomputing `lines`.
  const [openTurnId, setOpenTurnId] = useState<string | null>(null);
  const openLine = lines.find((l): l is Extract<Line, { turnId: string }> => 'turnId' in l && l.turnId === openTurnId);

  /**
   * A learner's line, with their errors marked in the adult register.
   *
   * §5 forbids the *tutor* naming an error; this is the transcript afterwards,
   * which is a different act. It is still adult-only — §11 keeps the kid
   * transcript hidden by default and §8.2 forbids the face signalling error,
   * and marking a child's mistakes in scrollback is that signal by another
   * route.
   *
   * The mark and the gloss render together or not at all. An underline with
   * nothing beside it is a red pen, and the pairing is the whole justification
   * for marking a learner's words.
   */
  const learnerText = (l: Extract<Line, { who: 'learner' }>) => {
    if (register === 'kid') return l.text;
    const pieces = markUp(l.text, marks[l.turnId] ?? []);
    return pieces.map((p, i) =>
      p.mark ? (
        <ruby key={i} className="decoration-warning underline decoration-wavy">
          {p.text}
          <rt className="text-warning text-xs">{p.mark.targetForm}</rt>
        </ruby>
      ) : (
        <span key={i}>{p.text}</span>
      ),
    );
  };

  return (
    <div className="my-4 flex flex-col gap-1">
      {lines.map((l, i) => {
        // §5: offered, not only asked for. Computed per line -- not just for
        // whichever one has its panel open -- because this is what decides
        // whether the line shows the worded invitation below instead of the
        // bare icon every other line gets.
        const offersSayInEnglish =
          l.who !== 'error' &&
          aidsFor({
            who: l.who,
            register,
            hasNativeLanguage: nativeLanguage != null,
            translationTarget: l.translationTarget,
          }).includes('sayInEnglish');
        return (
          <div key={i} className={clsx('chat', l.who === 'learner' ? 'chat-end' : 'chat-start')}>
            {l.who === 'tutor' && character && <BubbleFace character={character} expression="neutral" phase={phase} />}
            {l.who === 'tutor' && character && <div className="chat-header text-xs">{character.displayName}</div>}
            {l.who === 'learner' && learner && (
              <div className="chat-image">
                <LearnerPicture
                  picture={learner.picture}
                  displayName={learner.displayName}
                  learnerId={learner.id}
                  size={32}
                />
              </div>
            )}
            {l.who === 'learner' && <span className="sr-only">{t('transcript-you-said')}</span>}
            <div
              className={clsx(
                'chat-bubble',
                l.who === 'learner' && 'chat-bubble-primary',
                // Its own colour, not the tutor's. An error is the session
                // reporting a failure, and dressing it as speech was a lie about
                // where it came from.
                l.who === 'error' && 'chat-bubble-error',
              )}
            >
              {/* A learner's line may be in their own language (an off-target
                  turn), so it claims no language and takes its direction
                  from its own first letter. The tutor only speaks English. */}
              {l.who === 'learner' ? (
                <span dir="auto">{learnerText(l)}</span>
              ) : l.who === 'tutor' ? (
                <En>{l.text}</En>
              ) : (
                l.text
              )}
            </div>
            {/* `chat-footer`, daisyUI's own slot below a bubble, keeps this out
                of the text flow above — the transcript still reads as a
                conversation, not a conversation interrupted by a control. An
                error line gets none: it is this screen's own message, not
                something said, and `aidsFor` would offer it nothing anyway. */}
            {l.who !== 'error' && (
              <div className="chat-footer">
                {offersSayInEnglish ? (
                  // The offer itself, worded rather than iconified: §5's
                  // whole point is that this line does not wait to be
                  // discovered as a small icon among many identical ones.
                  // Opens the same panel the icon does (`AidsPanel` below),
                  // already targeting English (`l.translationTarget`, read
                  // straight from the server) -- this is a second way to
                  // reach it, not a second surface.
                  //
                  // `aria-label` still names the line, matching the icon
                  // button's own `aria-label` below: the visible text alone
                  // would repeat verbatim across every line this fires on,
                  // and a screen reader listing buttons would have nothing
                  // to tell them apart by.
                  <button
                    type="button"
                    className="btn btn-ghost btn-xs min-h-(--hit-min) text-info gap-1"
                    aria-label={t('transcript-say-in-english-label', { line: preview(l.text) })}
                    onClick={() => setOpenTurnId(l.turnId)}
                  >
                    <Languages aria-hidden className="size-3.5" />
                    {t('transcript-say-in-english')}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-ghost btn-xs min-h-(--hit-min) text-base-content/50 hover:text-base-content"
                    aria-label={t('transcript-help-label', { line: preview(l.text) })}
                    onClick={() => setOpenTurnId(l.turnId)}
                  >
                    <Languages aria-hidden className="size-3.5" />
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* The reply as it arrives, in the bubble it will keep. `TutorTurnCommitted`
          then replaces it with a committed line of identical shape, so the only
          visible change at commit is the cursor going away.

          `aria-hidden`, deliberately. This updates once per token; a live region
          here would read a word, interrupt itself, and read it again for the
          length of every reply. The committed line is what a screen reader
          meets, and the audio is playing regardless — this is a spoken app. */}
      {/* The tutor has been asked and has not begun. Its own bubble rather than
          a spinner somewhere else on the screen: this is the place the learner
          is already looking, and a greeting that arrives after several seconds
          of a screen reading "idle" under a button saying "Hold to talk" reads
          as a product that did not hear them.

          The same `chat-start` bubble the words will land in, so the dots are
          replaced in place rather than the conversation jumping when the first
          token arrives.

          `aria-live` rather than `aria-hidden`: unlike the streaming reply,
          this announces once and says something a screen reader user cannot
          otherwise know — that waiting is the right thing to do. */}
      {awaiting && (
        <div className="chat chat-start">
          {character && <BubbleFace character={character} expression="speaking" phase={phase} />}
          {character && <div className="chat-header text-xs">{character.displayName}</div>}
          <div className="chat-bubble" role="status" aria-live="polite">
            <span className="sr-only">
              {warming
                ? t('transcript-warming')
                : character
                  ? t('transcript-about-to-speak', { name: character.displayName })
                  : t('transcript-please-wait')}
            </span>
            <span aria-hidden className="flex items-center gap-1 py-1">
              <span className="bg-base-content/40 size-2 animate-bounce rounded-full [animation-delay:-0.3s]" />
              <span className="bg-base-content/40 size-2 animate-bounce rounded-full [animation-delay:-0.15s]" />
              <span className="bg-base-content/40 size-2 animate-bounce rounded-full" />
            </span>
            {/* Said out loud, not just to a screen reader. A warm reply starts
                in about 1.7s and a cold one took 31s; three dots mean "any
                second now" and are a lie for the length of a model load.
                Naming the cause also names its end -- "the first time" is why
                the next one will not do this. */}
            {warming && (
              <span aria-hidden className="block max-w-xs text-xs opacity-70">
                {t('transcript-warming-visible')}
              </span>
            )}
          </div>
        </div>
      )}

      {reply && (
        <div className="chat chat-start" aria-hidden="true">
          {character && <BubbleFace character={character} expression="speaking" phase={phase} />}
          {character && <div className="chat-header text-xs">{character.displayName}</div>}
          <div className="chat-bubble">
            <En>{reply}</En>
            <span className="ms-0.5 inline-block w-1 animate-pulse">▍</span>
          </div>
        </div>
      )}

      {/* The learner's words as they are heard, below the reply they may be
          talking over -- the order things were said. The bubble the committed
          line will land in, muted until it does.

          `aria-hidden`, as the streaming reply is and for the same reason: it
          grows every second, and the committed line is what a screen reader
          meets. English by construction -- the preview is transcribed with
          the language pinned -- so `<En>`, not the `dir="auto"` a committed
          learner line takes. No face beside it: a face is what marks a
          committed turn (learner picture PRD §3.3). An empty `chat-image`
          in its place all the same, because `chat-end` is a `1fr auto`
          grid: without the 32 px column the bubble sits flush right and
          jumps left the moment the turn commits beside its face. */}
      {partial && (
        <div className="chat chat-end" aria-hidden="true">
          {learner && <div className="chat-image size-8" />}
          <div className="chat-bubble chat-bubble-primary opacity-60">
            <En>{partial}</En>
            <span className="ms-0.5 inline-block w-1 animate-pulse">▍</span>
          </div>
        </div>
      )}

      {openLine && (
        // Keyed by turn id, not just addressed by the `turnId` prop: without
        // this, switching which line is open reuses the same `AidsPanel`
        // instance, and its child's local `justTranslated`/`failed` state
        // (neither derived from `turnId`) would survive the switch and render
        // one line's translation under another's quoted text. The key forces
        // a fresh mount per turn instead of trusting every piece of state
        // three components down to re-derive itself correctly.
        <AidsPanel
          key={openLine.turnId}
          turnId={openLine.turnId}
          text={openLine.text}
          aids={aidsFor({
            who: openLine.who,
            register,
            hasNativeLanguage: nativeLanguage != null,
            translationTarget: openLine.translationTarget,
          })}
          // The server's own answer, not a value derived here -- see
          // `aidsFor`'s `translationTarget` doc comment for why a second
          // derivation is exactly the bug this shape exists to rule out.
          translateInto={openLine.translationTarget}
          corrections={correctionsFor({ who: openLine.who, register, marks: marks[openLine.turnId] })}
          onClose={() => setOpenTurnId(null)}
        />
      )}
    </div>
  );
}
